import * as React from 'react';

import { isImageFile, type NativeFile } from '@gitbook/app-database';
import { BLOCK_FILE, BLOCK_IMAGES, files, images } from '@gitbook/doc-core';
import { spacing } from '@gitbook/react-uikit';
import { Block, BlockRange, type Block as BlockType, type Change } from '@gitbook/slate';
import { type EditorContainer, getEventTransfer, type Plugin } from '@gitbook/slate-react';
import { toast } from '@gitbook/spine-react';
import { Logger } from '@gitbook/util-debug';
import { useAPI } from '@gitbook/util-react-hooks';

import { useUploadFiles } from '../../Files';
import type { ContentEditorContextValue } from '../contexts';

const logger = Logger('app:useFileDropPlugin');

/**
 * Threshold for detecting when drag is near block edge (in pixels).
 * When dragging within this distance from top or bottom, we show drop lines.
 */
const EDGE_THRESHOLD_PX = spacing.small * 2;

/**
 * Plugin to handle native file drops between blocks.
 */
export function useFileDropPlugin(editorContext: ContentEditorContextValue): Plugin {
    const {
        logAnalyticsEvent,
        blocks: { withImages, withFiles },
    } = editorContext;

    const { uploadAndInsertFiles } = useUploadFiles();

    // Track if files are being dragged (for this plugin instance only)
    const isDraggingFilesRef = React.useRef(false);

    // Track current drop target for cleanup
    const currentDropTargetRef = React.useRef<{
        element: Element;
        position: 'before' | 'after';
    } | null>(null);

    /**
     * Clear the current drop target's data attributes.
     */
    const clearDropTarget = (): void => {
        if (currentDropTargetRef.current) {
            const attr =
                currentDropTargetRef.current.position === 'before'
                    ? 'data-file-drop-before'
                    : 'data-file-drop-after';
            currentDropTargetRef.current.element.removeAttribute(attr);
            currentDropTargetRef.current = null;
        }
    };

    /**
     * Set data attributes on the drop target element for visual feedback.
     */
    const setDropTarget = (element: Element, position: 'before' | 'after'): void => {
        // Clear old target if it's a different element or different position
        if (
            currentDropTargetRef.current &&
            (currentDropTargetRef.current.element !== element ||
                currentDropTargetRef.current.position !== position)
        ) {
            clearDropTarget();
        }

        // Set new target if not already set
        if (!currentDropTargetRef.current || currentDropTargetRef.current.element !== element) {
            const attr = position === 'before' ? 'data-file-drop-before' : 'data-file-drop-after';
            element.setAttribute(attr, '');
            currentDropTargetRef.current = { element, position };
        }
    };

    const onFilesDrop = (
        droppedFiles: File[],
        targetBlock: BlockType,
        position: 'before' | 'after',
        _change: Change,
        editor: EditorContainer
    ): boolean => {
        // Separate images from other files
        const [imageFiles, otherFiles] = droppedFiles.reduce<[NativeFile[], NativeFile[]]>(
            ([images, files], file) => {
                if (isImageFile(file)) {
                    if (withImages) {
                        return [images.concat(file), files];
                    }
                } else {
                    if (withFiles) {
                        return [images, files.concat(file)];
                    }
                }

                return [images, files];
            },
            [[], []]
        );

        // Nothing to drop (or nothing supported by the editor)
        if (imageFiles.length === 0 && otherFiles.length === 0) {
            return false;
        }

        const areAllImages = imageFiles.length > 0 && otherFiles.length === 0;

        // Upload files
        Promise.all([uploadAndInsertFiles(imageFiles), uploadAndInsertFiles(otherFiles)])
            .then(([imageRefs, fileRefs]) => {
                const totalRefsCount = imageRefs.length + fileRefs.length;

                // Uploads may have failed / been ignored because of their size/file type
                if (totalRefsCount === 0) {
                    return;
                }

                try {
                    editor.change((change) => {
                        let lastInsertedBlockKey: string | null = null;

                        // Insert images as a block
                        if (imageRefs.length > 0) {
                            const imagesBlock = images.createImagesBlock(imageRefs);
                            if (position === 'before') {
                                change.insertNodeBeforeByKey(targetBlock.key, imagesBlock);
                            } else {
                                change.insertNodeAfterByKey(targetBlock.key, imagesBlock);
                            }
                            lastInsertedBlockKey = imagesBlock.key;

                            // Select the new block
                            change.selectBlock(
                                BlockRange.create({
                                    anchorBlock: imagesBlock.key,
                                    focusBlock: imagesBlock.key,
                                })
                            );
                        }

                        // Insert file blocks
                        fileRefs.forEach((ref) => {
                            if (ref.kind !== 'file') {
                                return;
                            }

                            const fileBlock = files.createFileBlock(ref);
                            // Insert after the last inserted block, or relative to targetBlock.key if it's the first
                            if (lastInsertedBlockKey) {
                                change.insertNodeAfterByKey(lastInsertedBlockKey, fileBlock);
                            } else {
                                if (position === 'before') {
                                    change.insertNodeBeforeByKey(targetBlock.key, fileBlock);
                                } else {
                                    change.insertNodeAfterByKey(targetBlock.key, fileBlock);
                                }
                            }
                            lastInsertedBlockKey = fileBlock.key;
                        });

                        change.focus();
                    });

                    logAnalyticsEvent({
                        type: 'edit_page_document_insert_block',
                        blockType: areAllImages ? BLOCK_IMAGES : BLOCK_FILE,
                        images: imageRefs.length,
                        files: fileRefs.length,
                    });
                } catch (error) {
                    logger.error(
                        'Error inserting blocks after file upload - target block may have been deleted',
                        {
                            error,
                            targetBlockKey: targetBlock.key,
                        }
                    );
                    // Return early without inserting blocks or logging analytics
                    return;
                }
            })
            .catch((error) => {
                logger.error('Error uploading files', error);
                toast.error('Error while uploading files', error);
            });

        return true;
    };

    const onDragEnter = (
        event: React.SyntheticEvent<HTMLElement, Event> | Event,
        _change: Change,
        _editor: EditorContainer
    ): void => {
        const nativeEvent = 'nativeEvent' in event ? event.nativeEvent : event;
        const dataTransfer = (nativeEvent as DragEvent).dataTransfer;

        if (hasFiles(dataTransfer)) {
            isDraggingFilesRef.current = true;
        }
    };

    const onDragOver = (
        event: React.SyntheticEvent<HTMLElement, Event> | Event,
        _change: Change,
        editor: EditorContainer
    ): void => {
        // Only handle if files are being dragged
        if (!isDraggingFilesRef.current) {
            return;
        }

        const nativeEvent = 'nativeEvent' in event ? event.nativeEvent : event;
        const dragEvent = nativeEvent as DragEvent;
        const dataTransfer = dragEvent.dataTransfer;

        // Verify files are still being dragged
        if (!hasFiles(dataTransfer)) {
            isDraggingFilesRef.current = false;
            return;
        }

        // Find the block element we're hovering over
        const targetElement = dragEvent.target as Element | null;
        const blockElement = findBlockElement(targetElement);

        if (!blockElement) {
            // Don't prevent default - let other plugins handle it
            return;
        }

        // Find the Slate block node
        const blockKey = blockElement.getAttribute('data-key');
        if (!blockKey) {
            // Don't prevent default - let other plugins handle inline drops
            return;
        }

        const block = editor.value.document.getNode(blockKey);
        if (!block || !Block.isBlock(block)) {
            // Don't prevent default - let other plugins handle it
            return;
        }

        // Calculate drop position relative to block
        const position = calculateDropPosition(dragEvent as DragEvent, blockElement);

        if (position) {
            // Near edge - prevent default to allow drop
            // Set data attributes for visual feedback
            setDropTarget(blockElement, position);
            event.preventDefault();
        } else {
            // Not near edge - clear any existing drop target
            clearDropTarget();
        }
        // If not near edge, don't prevent default - let paste plugin handle inline drops
    };

    const onDragLeave = (
        event: React.SyntheticEvent<HTMLElement, Event> | Event,
        _change: Change,
        editor: EditorContainer
    ): void => {
        // Only clear if we're actually leaving the editor area
        const nativeEvent = 'nativeEvent' in event ? event.nativeEvent : event;
        const dragEvent = nativeEvent as DragEvent;
        const relatedTarget = dragEvent.relatedTarget;
        const elementRef = editor.elementRef?.current;

        if (elementRef && relatedTarget && !elementRef.contains(relatedTarget as Node)) {
            isDraggingFilesRef.current = false;
            clearDropTarget();
        }
    };

    const onDrop = (
        event: React.SyntheticEvent<HTMLElement, Event> | Event,
        change: Change,
        editor: EditorContainer
    ): boolean | undefined => {
        // Only handle if files are being dragged
        if (!isDraggingFilesRef.current) {
            return;
        }

        try {
            const nativeEvent = 'nativeEvent' in event ? event.nativeEvent : event;
            const dragEvent = nativeEvent as DragEvent;
            const transfer = getEventTransfer(dragEvent);

            // Verify files are in the transfer
            if (transfer.type !== 'files' || !transfer.files || transfer.files.length === 0) {
                return;
            }

            // Find the block element where the drop occurred
            const targetElement = dragEvent.target as Element | null;
            const blockElement = findBlockElement(targetElement);

            if (!blockElement) {
                // No block element, let other plugins handle it (e.g., paste plugin)
                return;
            }

            const blockKey = blockElement.getAttribute('data-key');
            if (!blockKey) {
                // No block key, let other plugins handle inline drops
                return;
            }

            const targetBlock = editor.value.document.getNode(blockKey);
            if (!targetBlock || !Block.isBlock(targetBlock)) {
                return;
            }

            // Calculate drop position
            const position = calculateDropPosition(dragEvent, blockElement);
            if (!position) {
                // Not near edge, let paste plugin handle inline drops
                return;
            }

            // Prevent default handling - we're now certain we'll handle this drop
            event.preventDefault();

            // Call the callback if provided
            if (onFilesDrop) {
                const result = onFilesDrop(transfer.files, targetBlock, position, change, editor);

                // If callback returns true, we've handled it
                if (result === true) {
                    return true;
                }
            }
        } finally {
            // Always clean up state and visual feedback, regardless of which code path was taken
            isDraggingFilesRef.current = false;
            clearDropTarget();
        }
    };

    return useAPI({
        onDragEnter,
        onDragOver,
        onDragLeave,
        onDrop,
    });
}

/**
 * Check if the dataTransfer contains files (not internal drag).
 */
function hasFiles(dataTransfer: DataTransfer | null): boolean {
    if (!dataTransfer) {
        return false;
    }

    // Check for files in dataTransfer
    if (dataTransfer.files && dataTransfer.files.length > 0) {
        return true;
    }

    // Check for file types in dataTransfer.types
    const types = Array.from(dataTransfer.types || []);
    const hasFileTypes = types.some(
        (type) => type === 'Files' || type === 'application/x-moz-file'
    );

    // Also check that it's not an internal drag (would have fragment or node)
    const hasInternalDrag = types.some(
        (type) => type === 'slate/fragment' || type === 'slate/node'
    );

    return hasFileTypes && !hasInternalDrag;
}

/**
 * Find the nearest block element from a DOM element.
 */
function findBlockElement(element: Element | null): Element | null {
    if (!element) {
        return null;
    }

    // Look for block wrapper or block content element
    const blockElement = element.closest('[data-key]');
    return blockElement;
}

/**
 * Calculate if drag position is near block edge and determine position.
 */
function calculateDropPosition(event: DragEvent, blockElement: Element): 'before' | 'after' | null {
    const rect = blockElement.getBoundingClientRect();
    const y = event.clientY;

    // Defensive, because some browser don't set the clientY properly
    if (typeof y !== 'number') {
        return null;
    }

    const relativeY = y - rect.top;

    // Check if we're near the top edge
    if (relativeY < EDGE_THRESHOLD_PX) {
        return 'before';
    }

    // Check if we're near the bottom edge
    if (relativeY > rect.height - EDGE_THRESHOLD_PX) {
        return 'after';
    }

    // In the middle, return null to indicate we're not near an edge
    return null;
}
